import React, { useEffect, useState } from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import './SurveyJobDataCuts.scss';
import SubHeaderSearch from '../../JobMatching/ResultDetails/SubHeaderSearch';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { getGenericRow } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { DataCutDetails, MONETARY_TYPE } from 'ps-components/AdvancedTable/GenericGrid/GenericGridCellRenderers';
import { bindActionCreators } from 'redux';
import { hideSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { connect, useDispatch } from 'react-redux';
import * as SurveyJobDataCutsActions from './SurveyJobDataCutActions';
import SuccessTransitionText from 'ps-components/Util/SuccessTransitionText';
import { Alert } from 'ps-design';
import classnames from 'classnames';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import { convertAnnualToRoundedPayType } from 'ps-components/Modules/JobRanges/Utils/Utils';
import { getRoundedValue } from '../../CompensationSummary/payElementMappings';
import { jobMatchGridActions } from '../LandingPage/JobMatchDetail/JobMatchGridCreate';
import { useAppState } from '../../../hooks/redux';

SurveyJobDataCuts.propTypes = {
  currentJob: object,
  addMatch: func,
  deleteMatch: func,
  survey: object,
  actions: shape({
    hideSlideout: func,
    getSurveyJobDataCuts: func,
    saveDataCutChanges: func
  }),
  slideout: object,
  isLoadingSurveyJobDataCuts: bool,
  benchmarkYear: number,
  displayIn: string,
  isSaving: bool,
  hasErrorSaving: bool,
  isSaveComplete: bool,
  monetaryRounding: number,
  selectedSurveys: array,
  addSelectedCutID: func,
  removeSelectedCutID: func,
  readOnly: bool,
  onMatchSelect: func,
  onBack: func,
  onSave: func
};

function SurveyJobDataCuts(props) {
  const [selectedDataCuts, setSelectedDataCuts] = useState([]);
  const [showAll, setShowAll] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [availableDataCuts, setAvailableDataCuts] = useState([]);
  const [selectedElementGroup, setSelectedElementGroup] = useState('baseSalary')
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [showSuccessText, setShowSuccessText] = useState(false);
  const [saveSuccessTimeout, setSaveSuccessTimeout] = useState(0);
  const [hideSlideoutTimeout, setHideSlideoutTimeout] = useState(0);
  const { survey } = props;
  const dispatch = useDispatch();
  const appState = useAppState()

  const RETURN_TO_ALL = 'Return to all suggested matches';

  useEffect(() => {
    if (props.slideout.open) {
      getSurveyJobDataCuts();
    } else {
      setIsInitialLoadComplete(false);
      setSearchText('');
      setShowAll(true);
      setAvailableDataCuts([]);
      setSelectedDataCuts([]);
      setShowSuccessText(false);
      clearTimeout(saveSuccessTimeout);
      clearTimeout(hideSlideoutTimeout);
    }
  }, [props.slideout]);

  useEffect(() => {
    if (props.isSaveComplete) {
      setShowSuccessText(true);
      const saveSuccessTimeout = setTimeout(() => {
        setShowSuccessText(false);
      }, 2000);
      const hideSlideoutTimeout = setTimeout(() => {
        props.actions.hideSlideout();
      }, 2500);
      setSaveSuccessTimeout(saveSuccessTimeout);
      setHideSlideoutTimeout(hideSlideoutTimeout);
    }
  }, [props.isSaveComplete]);

  const surveyCode = survey?.field_survey_code || survey.survey_code_default;
  const jobCode = survey?.field_job_code || survey.job_code_default;
  const currency = survey?.field_currency || survey.currency_default;

  function getSurveyJobDataCuts() {
    props.actions.getSurveyJobDataCuts(surveyCode, jobCode, currency).then(dataCuts => {
      if (!isInitialLoadComplete) {
        const selectedSurveys = props.selectedSurveyIds || props.selectedSurveys;
        const selectedDataCuts = dataCuts.filter(cut => selectedSurveys.includes(cut.cutId));
        setSelectedDataCuts(selectedDataCuts);
      }
      setIsInitialLoadComplete(true);
      setAvailableDataCuts(dataCuts);
    });
  }

  function toggleShowAll() {
    setShowAll(!showAll);
  }

  function clearSearch() {
    setSearchText('');
  }

  function getColumnClassNames(row) {
    const classNames = classnames({
      'data-cut__cell--margin-top': row.original.isDefault,
      'data-cut__cell--bold': getCutIds(selectedDataCuts).includes(row.original.cutId)
    });
    return classNames;
  }

  function getColumnHeaders() {
    const dataElementGroupMap = {
      baseSalary: 'Base Salary',
      totalCash: 'Total Cash',
      totalTargetCash: 'Total Target Cash',
      totalDirect: 'Total Direct Compensation',
      totalGuaranteedPay: 'Total Guaranteed Pay'
    }
    let columns = [
      {
        Header: 'Data Cut',
        customRender: DataCutDetails,
        width: 150,
        accessor: 'jobScope',
        getClassNames: getColumnClassNames
      },
      { Header: 'Is Default', width: 50, accessor: 'isDefault', getClassNames: getColumnClassNames },
      { Header: 'Currency', accessor: 'currency', width: 50, getClassNames: getColumnClassNames, centerAlign: true },
      {
        Header: dataElementGroupMap[selectedElementGroup],
        columns: [
          {
            Header: '25th',
            accessor: getMonetaryDisplay.bind(this, `${selectedElementGroup}25th`),
            width: 50,
            getClassNames: getColumnClassNames,
            centerAlign: true,
            type: MONETARY_TYPE,
            currency_default: survey.currency
          },
          {
            Header: '50th',
            accessor: getMonetaryDisplay.bind(this, `${selectedElementGroup}50th`),
            width: 50,
            getClassNames: getColumnClassNames,
            centerAlign: true,
            type: MONETARY_TYPE,
            currency_default: survey.currency
          },
          {
            Header: 'Avg',
            accessor: getMonetaryDisplay.bind(this, `${selectedElementGroup}Avg`),
            width: 50,
            getClassNames: getColumnClassNames,
            centerAlign: true,
            type: MONETARY_TYPE,
            currency_default: survey.currency
          },
          {
            Header: '75th',
            accessor: getMonetaryDisplay.bind(this, `${selectedElementGroup}75th`),
            width: 50,
            getClassNames: getColumnClassNames,
            centerAlign: true,
            type: MONETARY_TYPE,
            currency_default: survey.currency
          }
        ]
      }
    ];
    return columns;
  }

  function getMonetaryDisplay(fieldName, rowData) {
    const value = rowData[fieldName];
    if (!value) {
      return value;
    }
    const convertedPay = convertAnnualToRoundedPayType(value, props.displayIn);
    const rounded = getRoundedValue(convertedPay, 'monetary', props.monetaryRounding);
    return rounded.replaceAll(',', '');
  }

  function selectAccessor(dataCut) {
    return dataCut?.original?.cutId;
  }

  function isChecked(cutId) {
    return getCutIds(selectedDataCuts).includes(cutId);
  }

  function onCheck(cutId) {
    let newSelectedDataCuts;
    const selectedDataCutIds = getCutIds(selectedDataCuts);
    if (selectedDataCutIds.includes(cutId)) {
      newSelectedDataCuts = selectedDataCuts.filter(cut => cut.cutId !== cutId);
    } else {
      const dataCut = availableDataCuts.find(cut => cut.cutId === cutId);
      if (dataCut) {
        newSelectedDataCuts = [...selectedDataCuts, dataCut];
      }
    }
    setSelectedDataCuts(newSelectedDataCuts);
  }

  function isAllSelected() {
    return showAll ? availableDataCuts.every(cut => getCutIds(selectedDataCuts).includes(cut.cutId)) : true;
  }

  function getCutIds(dataCuts) {
    return dataCuts.map(cut => cut.cutId);
  }

  function getRemovedDataCutIds() {
    const selectedSurveys = props.selectedSurveyIds || props.selectedSurveys;
    const addedCutIdsThatAreAvailable = selectedSurveys.filter(cutId =>
      getCutIds(availableDataCuts).includes(cutId)
    );
    return addedCutIdsThatAreAvailable.filter(cutId => !getCutIds(selectedDataCuts).includes(cutId));
  }

  function getAddedDataCuts() {
    const selectedSurveys = props.selectedSurveyIds || props.selectedSurveys;
    return selectedDataCuts.filter(cut => !selectedSurveys.includes(cut.cutId));
  }

  function getAddedDataCutIds() {
    return getCutIds(getAddedDataCuts());
  }

  function renderDataCutChangeText() {
    const removedDataCutCount = getRemovedDataCutIds().length;
    const addedDataCutCount = getAddedDataCutIds().length;
    const text = [];
    if (addedDataCutCount > 0) {
      text.push(<span>{`${addedDataCutCount} new ${props.selectionChangeType || 'match'}${addedDataCutCount > 1 && props.selectionChangeType === 'data cut' ? 's' : addedDataCutCount > 1 ? 'es' : ''} will be added`}</span>);
    }
    if (removedDataCutCount > 0) {
      text.push(
        <span>{`${removedDataCutCount} current ${props.selectionChangeType || 'match'}${removedDataCutCount > 1 && props.selectionChangeType === 'data cut' ? 's' : removedDataCutCount > 1 ? 'es' : ''} will be removed`}</span>
      );
    }
    return <React.Fragment>{text}</React.Fragment>;
  }

  function saveDataCutChanges() {
    const removedDataCutIds = getRemovedDataCutIds();
    const addedDataCuts = getAddedDataCuts();
    const addedDataCutIds = getCutIds(addedDataCuts);
    if (props.shouldNotPersist) {
      if (addedDataCutIds.length > 0) {
         props.addSelectedCutID?.(addedDataCutIds);
       }

       if (removedDataCutIds.length > 0) {
         props.removeSelectedCutID?.(removedDataCutIds);
       }

       props?.onSave?.(addedDataCutIds, removedDataCutIds);

       props.actions.hideSlideout();
    } else {
      props.actions
        .saveDataCutChanges(props.currentJob, addedDataCutIds, removedDataCutIds, props.benchmarkYear, props.displayIn)
        .then(() => {
          if (props.updateModifiedDate) {
            props.updateModifiedDate(props.currentJob.id, props.benchmarkYear);
          }

          let currency = null;
          if (addedDataCutIds.length > 0) {
            props.addSelectedCutID?.(addedDataCutIds);
            currency = addedDataCuts[0].currency;
          }
          if (removedDataCutIds.length > 0) {
            props.removeSelectedCutID?.(removedDataCutIds);
        }
        props.onMatchSelect(currency);
        dispatch(jobMatchGridActions.setIsStale(props.currentJob.id, 'true'));
      });
    }
  }

  function getRowData() {
    if (showAll) {
      if (searchText) {
        return availableDataCuts.filter(cut => cut.jobScope.toLowerCase().includes(searchText.toLowerCase()));
      }
      return availableDataCuts;
    }
    return selectedDataCuts;
  }

  function hasChanges() {
    return getRemovedDataCutIds().length > 0 || getAddedDataCutIds().length > 0;
  }

  return (
    <React.Fragment>
      <div className="survey-data-cuts">
        <div className="survey-data-cuts-header">
          <div className="survey__header">
            <div className="header__title">Select Data Cuts for</div>
            <div className="survey-job-code">{jobCode}</div>
            <div className="survey-title">{props.survey.job_title_default || props.survey.field_job_title}</div>
            <div className="survey-info">
              {`${surveyCode} ${props.survey.survey_long_name_default || props.survey.field_pub_survey_short_name}`}
            </div>
          </div>
          <div className="header__actions">
            <SlideoutHeaderButton iconClass="icon-cancel" onClick={props.actions.hideSlideout} />
          </div>
        </div>
        <SlideoutBody>
          {props.hasErrorSaving && (
            <Alert
              className="pxl-margin-bottom-large"
              message="Could not save data cut changes. Please try again later."
              close={true}
            />
          )}

          {props.onBack && (
            <div className="return-link" onClick={props.onBack}>
              <i className="icon-reply" />
              {RETURN_TO_ALL}
            </div>
          )}

          <SubHeaderSearch
            clearSearch={clearSearch}
            isSelected={showAll}
            placeholderText="Search survey data cuts"
            searchText={searchText}
            selectedItems={selectedDataCuts}
            setSearchText={setSearchText}
            toggleSelected={toggleShowAll}
            dataCuts={getRowData()}
            session={appState.session}
            survey={props.survey}
            totalCount={getRowData().length}
            selectedElementGroup={selectedElementGroup}
            setSelectedElementGroup={setSelectedElementGroup}
            displayIn={props.displayIn}
            isDataCutSlideout
          />
          <AdvancedTable
            allowResize={true}
            allowSorting={true}
            columnHeaders={getColumnHeaders()}
            rowData={getRowData()}
            getDataRow={getGenericRow}
            dataLoading={props.isLoadingSurveyJobDataCuts}
            totalCount={getRowData().length}
            useSelectAll={false}
            useSelectAccessor={selectAccessor}
            isAllChecked={isAllSelected()}
            isChecked={isChecked}
            onCheck={onCheck}
            hiddenColumns={['isDefault']}
            hideSelect={props.readOnly}
          />
        </SlideoutBody>
      </div>
      <SlideoutFooter isBoxShadowTheme={true}>
        <div className="footer__text">{renderDataCutChangeText()}</div>
        <div className="footer__buttons">
          <SuccessTransitionText showText={showSuccessText} text="Save successful">
            <SlideoutFooterStandardButtons
              applyText={props.isSaving ? <i className="icon-animate-spin icon-spin"></i> : 'Save'}
              doApply={saveDataCutChanges}
              isApplyDisabled={props.isLoadingSurveyJobDataCuts || props.isSaving || !hasChanges() || props.readOnly}
              doCancel={props.actions.hideSlideout}
            />
          </SuccessTransitionText>
        </div>
      </SlideoutFooter>
    </React.Fragment>
  );
}

const mapStateToProps = ({ surveyJobDataCuts, marketPricing }) => ({
  isLoadingSurveyJobDataCuts: surveyJobDataCuts.isLoadingSurveyJobDataCuts,
  isSaving: surveyJobDataCuts.isSavingDataCutChanges,
  isSaveComplete: surveyJobDataCuts.isSavingDataCutChangesComplete,
  hasErrorSaving: surveyJobDataCuts.hasErrorSavingDataCutChanges,
  selectedSurveys: marketPricing.JobPricingReducer.MainJobPricingReducer.selectedCutIDs
});
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...SurveyJobDataCutsActions,
      hideSlideout
    },
    dispatch
  )
});
export default connect(mapStateToProps, mapDispatchToProps)(SurveyJobDataCuts);
